<template>
  <v-app class="futuristic-app">
    <v-main>
      <v-container fluid class="pa-4 pa-md-6 pa-lg-8">
        <router-view />
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
  export default {
    name: 'App',
    created() {
      this.$store.dispatch('get_active_exchanges')

      if (localStorage.getItem('currency') === null) {
        this.$store.state.CurrencyStore.browser_currency = 'KES'
        localStorage.setItem('currency', 'KES')
      }

      this.$store.commit('LOCAL_CURRENCY2', {
        currency_name: 'KES',
        currency_public_id: '162fface-f5f1-41de-913b-d2bb784dda3a'
      })
    }
  }
</script>

<style scoped>
  .futuristic-app {
    background: linear-gradient(135deg, #e8ecef 0%, #dfe4ea 100%);
    min-height: 100vh;
  }

  :deep(.v-application) {
    font-family: 'Roboto', sans-serif;
    background: linear-gradient(135deg, #e8ecef 0%, #dfe4ea 100%);
    font-size: 0.7rem; /* smaller base font */
  }

  /* ===== GLOBAL SPACING & BOX MODEL FIXES ===== */
  :deep(*),
  :deep(*::before),
  :deep(*::after) {
    box-sizing: border-box;
  }

  :deep(.v-input) {
    margin-bottom: 6px; /* reduced from 16px */
  }

  @media (max-width: 600px) {
    :deep(.v-input) {
      margin-bottom: 4px;
    }
  }

  :deep(.v-label) {
    max-width: 100%;
    overflow-wrap: anywhere;
    font-size: 0.9rem; /* increased from 0.65rem for textboxes */
  }

  :deep(.detail-item) {
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  /* ===== BOOKING.VUE CONTROL STYLES ===== */

  /* Headings */
  :deep(h1),
  :deep(h2),
  :deep(h3) {
    font-family: 'Earthwerk', sans-serif !important;
    letter-spacing: 1px;
    font-size: 1.1rem; /* reduced but not too small */
  }

  :deep(h4),
  :deep(h5),
  :deep(h6) {
    font-family: 'Knockout', sans-serif !important;
    letter-spacing: 1px;
    font-size: 0.95rem;
  }

  /* Knockout Header Class */
  :deep(.knockout-header) {
    font-family: 'Knockout';
    font-size: 16px; /* reduced from 24px */
    color: #74a533;
    letter-spacing: 1px;
    margin-bottom: 8px;
    padding-bottom: 4px;
    border-bottom: 1px solid #f0f0f0;
  }

  @media (max-width: 960px) {
    :deep(.knockout-header) {
      font-size: 14px;
    }
  }

  @media (max-width: 600px) {
    :deep(.knockout-header) {
      font-size: 13px;
    }
  }

  /* Arial Text Class */
  :deep(.arial-text) {
    font-family: 'Arial';
    letter-spacing: 0.3px;
    flex-grow: 1;
    padding-right: 4px;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
    margin: 2px 0;
    font-size: 11px; /* reduced */
    word-wrap: break-word;
  }

  @media (max-width: 960px) {
    :deep(.arial-text) {
      font-size: 10px;
    }
  }

  @media (max-width: 600px) {
    :deep(.arial-text) {
      font-size: 9px;
    }
  }

  /* Text Big Class */
  :deep(.text-big) {
    font-size: 16px; /* reduced */
  }

  /* Font Light Class */
  :deep(.font-light) {
    font-weight: 300;
  }

  /* Knockout Text Class */
  :deep(.knockout-text) {
    font-family: 'Knockout';
    font-size: 14px; /* reduced */
    letter-spacing: 0.3px;
    flex-grow: 1;
    padding-right: 6px;
  }

  /* Count Badge Class */
  :deep(.count-badge) {
    font-family: Arial, Helvetica, sans-serif;
    font-size: 12px;
    min-width: 20px;
    height: 20px;
    border-radius: 10px; /* keep rounded */
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 4px;
    flex-shrink: 0;
  }

  @media (max-width: 960px) {
    :deep(.count-badge) {
      font-size: 11px;
    }
  }

  @media (max-width: 600px) {
    :deep(.count-badge) {
      font-size: 10px;
      min-width: 18px;
      height: 18px;
    }
  }

  /* Print Badge Class */
  :deep(.print-badge) {
    font-family: Knockout;
    font-size: 12px;
    min-width: 20px;
    height: 20px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 4px;
    flex-shrink: 0;
  }

  /* White Badge Class */
  :deep(.white-badge) {
    background-color: rgba(255, 255, 255, 0.2);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.3);
  }

  /* Grey Badge Class */
  :deep(.grey-badge) {
    background-color: #f5f5f5;
    color: #333;
    border: 1px solid #e0e0e0;
  }

  /* White Text Class */
  :deep(.white--text) {
    color: white !important;
  }

  /* Black Text Class */
  :deep(.black--text) {
    color: #333 !important;
  }

  /* Date Range Class */
  :deep(.date-range) {
    font-family: Arial, sans-serif;
    font-size: 10px;
    margin-top: 2px;
    padding-top: 2px;
    border-top: 1px dashed #e0e0e0;
    word-wrap: break-word;
  }

  @media (max-width: 600px) {
    :deep(.date-range) {
      font-size: 9px;
    }
  }

  /* Pickup Info Class */
  :deep(.pickup-info) {
    font-style: italic;
  }

  /* Action Button Class */
  :deep(.action-btn) {
    min-width: 60px !important;
    height: 28px;
    margin-left: 6px;
    font-size: 11px;
    text-transform: uppercase;
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
    border-radius: 16px !important; /* keep rounded */
  }

  :deep(.action-btn:hover) {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12);
  }

  :deep(.action-btn:active) {
    transform: translateY(0);
  }

  /* Summary Card Class */
  :deep(.summary-card) {
    flex: 1;
    min-width: 200px;
    border-radius: 16px !important; /* keep rounded */
    transition: all 0.3s ease-in-out;
    margin-bottom: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
    overflow: hidden;
    position: relative;
    padding: 6px; /* reduced padding */
  }

  :deep(.summary-card:hover) {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15) !important;
  }

  :deep(.summary-card::before) {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 70%);
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }

  :deep(.summary-card:hover::before) {
    opacity: 1;
  }

  /* Compact Summary Card Class */
  :deep(.compact-summary-card) {
    border-radius: 16px !important; /* keep rounded */
    transition: all 0.3s ease-in-out;
    margin-bottom: 6px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08) !important;
    position: relative;
    width: 100%;
    min-width: 150px;
    max-width: 220px;
    margin-left: auto;
    margin-right: auto;
    padding: 6px; /* reduced */
  }

  :deep(.compact-summary-card:hover) {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12) !important;
  }

  :deep(.compact-summary-card .summary-item) {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 0 4px;
    flex-wrap: wrap;
    gap: 4px;
  }

  :deep(.compact-summary-card .date-range) {
    padding: 0 4px;
    margin-top: 3px;
    font-size: 10px;
    word-wrap: break-word;
  }

  @media (max-width: 960px) {
    :deep(.compact-summary-card) {
      max-width: 100%;
      min-width: 0;
    }
  }

  @media (max-width: 600px) {
    :deep(.compact-summary-card) {
      max-width: 100%;
      min-width: 0;
    }
  }

  /* Card Header Class */
  :deep(.card-header) {
    padding: 4px 8px; /* reduced */
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    position: relative;
    border-radius: 16px 16px 0 0; /* keep rounded */
    background: linear-gradient(145deg, rgba(255, 255, 255, 0.15), rgba(0, 0, 0, 0.15));
  }

  :deep(.card-header::after) {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }

  :deep(.compact-summary-card:hover .card-header::after) {
    left: 100%;
  }

  :deep(.card-header .arial-text) {
    font-size: 11px;
    margin: 1px 0;
  }

  /* Card Body Class */
  :deep(.card-body) {
    padding: 8px 10px; /* reduced */
    position: relative;
    background: linear-gradient(145deg, rgba(255, 255, 255, 0.1), rgba(0, 0, 0, 0.1));
    box-shadow: inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.1);
  }

  /* Earth Color Class */
  :deep(.earth-color) {
    color: #74a533;
  }

  /* Monospace Text Class */
  :deep(.monospace-text) {
    font-family: monospace;
  }

  /* White Card Class */
  :deep(.white-card) {
    background: white;
  }

  /* Panel Header Class */
  :deep(.panel-header) {
    padding: 4px 8px;
  }

  /* Panel Content Class */
  :deep(.panel-content) {
    padding: 8px;
  }

  /* Panel Subheader Class */
  :deep(.panel-subheader) {
    font-weight: bold;
    margin-bottom: 4px;
  }

  /* Detail Item Class */
  :deep(.detail-item) {
    margin-bottom: 4px;
    line-height: 1.3;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  /* Divider Class */
  :deep(.divider) {
    margin: 4px 0;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
  }

  /* Section Title Class */
  :deep(.section-title) {
    margin-top: 12px;
    margin-bottom: 8px;
  }

  /* Note Card Class */
  :deep(.note-card) {
    margin-bottom: 8px;
    border-radius: 16px;
  }

  /* Note Tag Class */
  :deep(.note-tag) {
    margin-bottom: 4px;
  }

  :deep(.note-tag.internal) {
    background: #f5f5f5;
    color: #666;
  }

  /* Note Content Class */
  :deep(.note-content) {
    font-size: 13px;
    line-height: 1.4;
  }

  /* Note Meta Class */
  :deep(.note-meta) {
    font-size: 10px;
    color: #999;
  }

  /* Status Chip Container */
  :deep(.status-chips) {
    margin-bottom: 10px;
  }

  /* Booking Ref Class */
  :deep(.booking-ref) {
    font-size: 18px;
    font-weight: bold;
  }

  /* Booking Meta Class */
  :deep(.booking-meta) {
    font-size: 11px;
    margin-top: 2px;
  }

  /* Client Detail Class */
  :deep(.client-detail) {
    margin-bottom: 2px;
    font-size: 11px;
  }

  /* School Details, Corporate Details, Partner Details */
  :deep(.school-details),
  :deep(.corporate-details),
  :deep(.partner-details) {
    margin-top: 8px;
  }

  /* Section Subtitle */
  :deep(.section-subtitle) {
    margin-top: 8px;
    padding-top: 4px;
    border-top: 1px solid rgba(255,255,255,0.2);
    font-weight: bold;
  }

  /* Status Select Class */
  :deep(.status-select) {
    margin-bottom: 8px;
  }

  /* Summary Panel Class */
  :deep(.summary-panel) {
    margin-bottom: 8px;
  }

  /* Compact List Class */
  :deep(.compact-list) {
    list-style: none;
    padding-left: 0;
  }

  /* Details List Class */
  :deep(.details-list) {
    padding-left: 10px;
  }

  /* Action Buttons Container */
  :deep(.action-buttons) {
    margin-top: 12px;
  }

  /* Button Group */
  :deep(.button-group) {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 6px;
  }

  /* Note Form Class */
  :deep(.note-form) {
    margin-bottom: 12px;
  }

  /* Note Input Class */
  :deep(.note-input) {
    margin-bottom: 6px;
  }

  /* Note Switch Class */
  :deep(.note-switch) {
    margin-bottom: 6px;
  }

  /* Submit Note Button Class */
  :deep(.submit-note-btn) {
    margin-top: 4px;
  }

  /* Log Table Class */
  :deep(.log-table),
  :deep(.transaction-table),
  :deep(.credit-note-table),
  :deep(.inspection-note-table),
  :deep(.residency-proof-table) {
    border-radius: 16px;
    overflow: hidden;
  }

  /* ===== END OF BOOKING.VUE CONTROL STYLES ===== */

  /* Card & Form styling - keep large border-radius, reduce size */
  :deep(.v-card),
  :deep(.v-form) {
    border-radius: 16px !important; /* keep rounded */
    background: linear-gradient(135deg, #ffffff, #f0f4e8);
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    padding: 8px; /* reduced padding */
  }

  /* Input colors */
  :deep(.theme--light.v-input input),
  :deep(.theme--light.v-input textarea) {
    color: #000000;
    font-size: 1rem; /* increased from 0.7rem for textboxes */
  }

  :deep(.v-label),
  :deep(.v-label--active) {
    color: #000000;
    font-size: 0.9rem; /* increased from 0.65rem for textboxes */
  }

  :deep(.v-list-item--active .v-list-item__title) {
    color: #587e27;
  }

  /* Data table header */
  :deep(.v-data-table thead tr th) {
    background: linear-gradient(45deg, #dfe4ea, #e8ecef);
    color: #587e27;
    font-weight: 600;
    font-size: 0.7rem;
    padding: 4px 8px;
  }

  :deep(.theme--light.v-data-table .v-data-table__empty-wrapper) {
    color: #587e27;
  }

  /* Slide-in animation - unchanged */
  :deep(.v-card),
  :deep(.v-form),
  :deep(.v-btn),
  :deep(.v-text-field .v-input__slot),
  :deep(.v-input__slot),
  :deep(.v-chip),
  :deep(.v-alert),
  :deep(.v-tab) {
    opacity: 0;
    transform: translateX(-50px);
    animation: slideIn 0.5s ease-out forwards;
    animation-delay: calc(var(--index, 0) * 0.1s);
  }

  @keyframes slideIn {
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  /* Button styling - keep large border-radius, reduce size */
  :deep(.v-btn) {
    border-radius: 16px !important; /* keep rounded */
    text-transform: none;
    font-weight: 500;
    color: #2c3e50;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease, background 0.5s ease-out;
    margin: 2px;
    position: relative;
    overflow: hidden;
    font-size: 0.7rem;
    padding: 0 12px;
    height: 28px; /* reduced height */
    min-width: 0;
  }

  :deep(.v-btn:hover),
  :deep(.v-btn--selected) {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.4), rgba(88, 126, 39, 0.15));
    box-shadow: 0 4px 12px rgba(88, 126, 39, 0.15);
    transform: translateY(-1px);
    filter: brightness(1.2);
  }

  :deep(.v-btn:hover::before),
  :deep(.v-btn--selected::before) {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent);
    transition: left 0.5s ease;
  }

  :deep(.v-btn:hover::before),
  :deep(.v-btn--selected::before) {
    left: 100%;
  }

  :deep(.v-btn:active) {
    background: linear-gradient(135deg, rgba(88, 126, 39, 0.1), rgba(88, 126, 39, 0.05));
    transform: translateY(0);
  }

  /* Text field / Input slot - keep large border-radius, reduce padding */
  :deep(.v-text-field .v-input__slot),
  :deep(.v-input__slot) {
    border-radius: 16px !important; /* keep rounded */
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1));
    backdrop-filter: blur(10px);
    border: 1px solid transparent;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
    transition: all 0.3s ease, background 0.5s ease-out;
    margin: 2px;
    padding: 2px 8px; /* reduced padding */
    min-height: 28px; /* reduced */
  }

  :deep(.v-text-field .v-input__slot .v-text-field__slot input),
  :deep(.v-input__slot .v-input__control input) {
    font-size: 1rem; /* increased from 0.7rem for textboxes */
    padding: 2px 4px;
  }

  :deep(.theme--light.v-text-field > .v-input__control > .v-input__slot:before) {
    border-color: transparent;
  }

  /* Dialog & Picker */
  :deep(.v-dialog .v-card) {
    border-radius: 16px;
  }

  :deep(.v-date-picker-table) {
    min-height: 200px;
  }

  :deep(.v-date-picker-table table) {
    height: 200px !important;
  }

  :deep(.v-date-picker-table .v-btn) {
    height: 28px !important;
    width: 28px !important;
    margin: 1px !important;
    font-size: 0.7rem;
    border-radius: 50% !important; /* keep round */
  }

  :deep(.v-date-picker-table .v-btn.v-btn--active) {
    background: #587e27;
    color: black;
    border-radius: 50% !important;
  }

  :deep(.v-picker__title) {
    background: linear-gradient(45deg, #dfe4ea, #e8ecef);
    border-radius: 16px 16px 0 0;
    padding: 4px 8px;
    font-size: 0.8rem;
  }

  /* Navigation & Table */
  :deep(.v-navigation-drawer) {
    background: linear-gradient(180deg, #ffffff 0%, #e8ecef 100%);
  }

  :deep(.v-data-table) {
    border-radius: 16px;
    background: #ffffff;
    overflow: hidden;
    font-size: 0.7rem;
  }

  :deep(.v-data-table thead tr th) {
    border-radius: 0;
  }

  :deep(.v-data-table thead tr th:first-child) {
    border-top-left-radius: 16px;
  }

  :deep(.v-data-table thead tr th:last-child) {
    border-top-right-radius: 16px;
  }

  /* Custom classes */
  :deep(.pantone) {
    background-color: #587e27;
    color: black;
    border-radius: 16px;
  }

  :deep(.musk) {
    background-color: #520036;
    color: black;
    border-radius: 16px;
  }

  :deep(.v-chip),
  :deep(.v-alert),
  :deep(.v-tabs),
  :deep(.v-tab) {
    border-radius: 16px;
    font-size: 0.7rem;
  }

  /* Links */
  :deep(a) {
    text-decoration: none;
    transition: all 0.3s ease, background 0.5s ease-out;
  }

  :deep(a:hover),
  :deep(a.selected) {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.4), rgba(88, 126, 39, 0.15));
    filter: brightness(1.2);
  }

  /* Responsive adjustments - keep border-radius but reduce sizes further on small screens */
  @media (max-width: 960px) {
    :deep(.v-card),
    :deep(.v-form) {
      border-radius: 14px;
      padding: 6px;
    }

    :deep(.v-btn) {
      border-radius: 14px;
      font-size: 0.65rem;
      height: 26px;
    }

    :deep(.v-text-field .v-input__slot),
    :deep(.v-input__slot) {
      border-radius: 14px;
      min-height: 26px;
    }
  }

  @media (max-width: 600px) {
    :deep(.v-card),
    :deep(.v-form) {
      border-radius: 12px;
      padding: 4px;
    }

    :deep(.v-btn) {
      border-radius: 12px;
      font-size: 0.6rem;
      height: 24px;
    }

    :deep(.v-text-field .v-input__slot),
    :deep(.v-input__slot) {
      border-radius: 12px;
      min-height: 24px;
    }
  }

  /* Balance classes */
  .balance-original {
    font-size: 16px;
    font-weight: bold;
  }

  .balance-converted {
    font-size: 22px;
    font-weight: bold;
  }

  /* Font face definitions */
  @font-face {
    font-family: 'Earthwerk';
    font-weight: bold;
    src: url('PizzaDude.dk-Earthwerk.otf');
  }

  @font-face {
    font-family: 'Akzidenz';
    font-weight: bold;
    src: url('akzidenz-grotesk-black.ttf');
  }

  @font-face {
    font-family: 'Knockout';
    font-weight: bold;
    src: url('knockout-htf-69-full-liteweight-1361516723.ttf');
  }
</style>
